import _ from "lodash";
import React from "react";
import classes from "./SubmissionTable.scss";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import I18nHOC from "UIComponents/I18nHOC";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import FieldComponent from "../FieldComponent";
import { downloadAttachmentZip } from "modules/Services";
import { getFieldContextFromParentType } from "UnitPlans/modules/Utils";
import { IconButton, Tooltip } from "@toddle-edu/ds-web";
import {
  DownloadOutlined,
  LockFilled,
  LockOpenOutlined,
} from "@toddle-edu/ds-icons";
import classNames from "classnames";
import { generateRandomId } from "Utils";
import { isAttachmentLocked } from "Utils/AttachmentHelpers";
import { getUniqueName } from "Projects/modules/Utils";
import {
  STUDENT,
  ENTITY_LOCKED_STATE,
  ENTITY_UNLOCKED_STATE,
} from "Constants/stringConstants";
import { getUserType } from "modules/CommonSelectors";
import ACLStore from "lib/aclStore";
import { Sentry } from "services/sentry";
import { ENABLE_PROJECTS_ZIP_DOWNLOAD_V2 } from "Constants/permissionConstants";

const styles = {
  gridRowStyle: {
    gridColumnGap: "24px",
    gridTemplateColumns: "404px repeat(2, 350px)",
  },
  containerStyle: {
    padding: "0 12px",
  },
};

const getTooltipLabel = ({ columnId }) => {
  switch (columnId) {
    case "draftFiles":
      return "project:label_all_draft_file_s";
    case "finalFiles":
      return "project:label_all_final_file_s";
    default:
      return "";
  }
};

class SubmissionTable extends UIBaseComponent {
  constructor(props) {
    super(props);
  }

  updateInputFieldLocally = ({ params, extraParams }) => {
    const { updateInputField } = this.props;
    updateInputField(params, extraParams);
  };

  onDownloadClick = async ({ zipFileName, attachments }) => {
    const { downloadAttachmentZip } = this.props;

    const input = {
      attachmentsObjects: {
        _: {
          attachments,
          folderId: generateRandomId(),
          folderName: "Draft Files",
        },
      },
      showAttachmentName: true,
      zipFileName,
    };
    await downloadAttachmentZip(input);
  };

  renderActions = ({ fields, actionId, actionConfig, columnId }) => {
    switch (actionId) {
      case "download": {
        const { getResolveLabelFromEntities, testId } = this.props;
        const {
          downloadIds = [],
          fileName = "download",
          submissionItemFileName = "",
          fileNameV2 = "",
        } = actionConfig;

        let attachments = [];
        const uniqueNameObj = {};
        _.forEach(downloadIds, downloadId => {
          const fieldData = _.find(fields, { uid: downloadId });
          const value = _.get(fieldData, "attachments", []);
          if (value) {
            // Filtering out attachments with links and renaming the attachments
            const updatedAttachments = _.reduce(
              value,
              (acc, item) => {
                if (item?.type !== "LINK") {
                  const attachmentName = getUniqueName({
                    name:
                      ACLStore.can(ENABLE_PROJECTS_ZIP_DOWNLOAD_V2) &&
                      getResolveLabelFromEntities &&
                      submissionItemFileName
                        ? getResolveLabelFromEntities({
                            label: submissionItemFileName,
                            columnId: downloadId,
                          })
                        : item.name,
                    nameObj: uniqueNameObj,
                  });
                  acc.push({
                    ...item,
                    name: attachmentName,
                  });
                }
                return acc;
              },
              []
            );
            attachments = _.concat(attachments, updatedAttachments);
          }
        });

        const isDisabled = _.isEmpty(attachments);
        // some templates may not have fileNameV2
        const zipName =
          ACLStore.can(ENABLE_PROJECTS_ZIP_DOWNLOAD_V2) && fileNameV2
            ? fileNameV2
            : fileName;
        return (
          <IconButton
            disabled={isDisabled}
            onClick={() =>
              this.onDownloadClick({
                zipFileName: getResolveLabelFromEntities
                  ? getResolveLabelFromEntities({ label: zipName })
                  : zipName,
                attachments,
              })
            }
            icon={<DownloadOutlined />}
            type="plain"
            variant="neutral"
            size={"medium"}
            testId={`${testId}-${columnId}-download`}
          />
        );
      }
      case "locking": {
        const {
          bulkLockSubmission,
          isUserStudent,
          disabled,
          t,
          disableSubmissionLockStatusUpdates,
          testId,
        } = this.props;
        const { lockingIds = [], lockingData = [] } = actionConfig;

        if (isUserStudent) {
          return null;
        }

        let attachments = [];

        _.forEach(lockingIds, lockingId => {
          const fieldData = _.find(fields, { uid: lockingId });
          const value = _.get(fieldData, "attachments", []);
          attachments = _.concat(attachments, value ? value : []);
        });

        const isDisabled =
          _.isEmpty(attachments) ||
          (disableSubmissionLockStatusUpdates ?? disabled);
        const showLock = !_.every(attachments, attachment =>
          isAttachmentLocked({ attachment })
        );

        const tooltipLabel = getTooltipLabel({ columnId });

        return (
          <Tooltip
            tooltip={t(tooltipLabel, {
              label: showLock ? t("common:lock") : t("common:unlock"),
            })}
          >
            <IconButton
              disabled={isDisabled}
              onClick={() =>
                bulkLockSubmission({
                  lockingOptionDetails: lockingData,
                  lockingState: showLock
                    ? ENTITY_LOCKED_STATE
                    : ENTITY_UNLOCKED_STATE,
                  attachments,
                })
              }
              icon={showLock ? <LockFilled /> : <LockOpenOutlined />}
              type="plain"
              variant="neutral"
              size={"medium"}
              testId={`${testId}-${columnId}-lockUnlock`}
            />
          </Tooltip>
        );
      }
      default:
        return null;
    }
  };

  fieldContext = getFieldContextFromParentType({
    parentType: this.props.fieldContextParentType,
  });

  renderEdit = () => {
    const {
      tableConfig = {},
      childrenList = [],
      mode,
      showSingleAttachment,
      getFieldValue,
      getUpdatedConfig,
      helpEnabledFields,
      getIsHelpActive,
      onHelpClick,
      t,
      updateSelectedTemplate,
      emptyText,
      sourceFrom,
      testId,
    } = this.props;

    const {
      columnIds = [],
      columnList = {},
      rowIds = [],
      rowList = {},
      gridRowStyle = styles.gridRowStyle,
    } = tableConfig;

    const rowSubtextStyles = classNames(
      { ["text-body-2"]: true },
      { [classes.rowSubtext]: true }
    );

    const emptyTextClasses = classNames(
      { ["text-body-1"]: true },
      { [classes.emptyText]: true }
    );

    const FieldContext = this.fieldContext;

    return (
      <FieldContext.Consumer>
        {({ fields }) => {
          return (
            <div className={classes.container}>
              <div className={classes.tableHeader} style={gridRowStyle}>
                {_.map(columnIds, columnId => {
                  const columnData = columnList[columnId];

                  const { actionList, label = "" } = columnData;
                  return (
                    <div
                      key={columnId}
                      className={classes.headerLabelContainer}
                    >
                      <div className="text-label"> {t(label)} </div>
                      <div className={classes.actionContainer}>
                        {_.map(actionList, action => {
                          const { actionId, actionConfig = {} } = action;
                          return this.renderActions({
                            fields,
                            actionId,
                            actionConfig,
                            columnId,
                          });
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className={classes.tableBody}>
                {_.map(rowIds, rowId => {
                  const rowData = rowList[rowId];
                  const rowDataColumnIds = _.get(rowData, "columnIds", []);
                  const { label = "", subtext = "" } = rowData;
                  return (
                    <div
                      key={rowId}
                      className={classes.tableRow}
                      style={gridRowStyle}
                    >
                      <div className={classes.rowLabelContainer}>
                        <div className="text-body"> {t(label)} </div>
                        <div className={rowSubtextStyles}>
                          {subtext && t(subtext)}
                        </div>
                      </div>
                      {_.map(rowDataColumnIds, dataId => {
                        if (dataId) {
                          const fieldDetails = _.find(childrenList, {
                            id: dataId,
                          });

                          const fieldData = _.find(fields, { uid: dataId });

                          if (
                            ACLStore.can(
                              "FeatureFlag:ProjectsTemplatesSentryDebugLog"
                            )
                          ) {
                            if (_.isEmpty(fieldData)) {
                              Sentry.captureMessage(
                                "PROJECT_DETAILS_SUBMISSION_TABLE_EMPTY_FIELDS",
                                {
                                  level: "info",
                                  extra: {
                                    fields,
                                    dataId,
                                    childrenList,
                                    fieldData,
                                  },
                                }
                              );
                            }
                          }

                          const { id: fieldId } = fieldData;

                          const { config = {}, type, id } = fieldDetails;

                          const updateConfig = getUpdatedConfig({
                            config,
                            fielduid: id,
                            type,
                          });

                          const value = getFieldValue({
                            type,
                            fielduid: id,
                          });

                          return (
                            <FieldComponent
                              {...updateConfig}
                              name={id}
                              key={id}
                              customRef={this.setFieldRef}
                              mode={mode}
                              fieldId={fieldId}
                              fieldKey={id}
                              type={type}
                              value={value}
                              showSingleAttachment={showSingleAttachment}
                              updateInputField={this.updateInputFieldLocally}
                              getFieldValue={getFieldValue}
                              getUpdatedConfig={getUpdatedConfig}
                              helpEnabledFields={helpEnabledFields}
                              getIsHelpActive={getIsHelpActive}
                              onHelpClick={onHelpClick}
                              updateSelectedTemplate={updateSelectedTemplate}
                              isLockingEnabled={true}
                              sourceFrom={sourceFrom}
                              testId={`${testId}-${id}`}
                              dsVersion="2.0"
                              containerStyle={styles.containerStyle}
                            />
                          );
                        } else {
                          return (
                            <div className={emptyTextClasses}>
                              {t(emptyText)}
                            </div>
                          );
                        }
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }}
      </FieldContext.Consumer>
    );
  };
}

SubmissionTable.defaultProps = {
  ...UIBaseComponent.defaultProps,
  showSingleAttachment: false,
  emptyText: "common:not_applicable",
};

const mapStateToProps = (state, ownProps) => {
  const userType = getUserType(state);

  const isUserStudent = _.isEqual(userType, STUDENT);

  return {
    isUserStudent,
  };
};

const mapActionCreators = {
  downloadAttachmentZip,
};

export default compose(
  I18nHOC({ resource: ["common", "project"] }),
  withRouter,
  connect(mapStateToProps, mapActionCreators)
)(SubmissionTable);
